import React, { FC, CSSProperties } from 'react';
import { FRAME } from '../../../lib/contactPhoto';

interface PhoneButtonPreviewProps {
  name: string;
  /** Saved photo, or a data: URL. Ignored when `background` is given. */
  photoUrl?: string;
  /** Live crop preview: a data: URL plus the CSS that shows only the cropped area. */
  background?: { dataUrl: string; backgroundSize: string; backgroundPosition: string };
  /** Width of the photo, in CSS px. The phone's frame is 184 x 148. */
  width?: number;
  placeholderText?: string;
  alt?: string;
}

/**
 * Looks like the senior phone's photo button (aidfone-android-2025
 * PhotoContactCard): white card, 8 dp padding, photo with 16 dp rounded
 * corners, bold dark name underneath. Scaled so the photo is `width` px wide.
 */
export const PhoneButtonPreview: FC<PhoneButtonPreviewProps> = ({
  name,
  photoUrl,
  background,
  width = FRAME.width,
  placeholderText,
  alt,
}) => {
  const s = width / FRAME.width;
  const photoStyle: CSSProperties = {
    width,
    height: width * (FRAME.height / FRAME.width),
    borderRadius: 16 * s,
  };

  let photo: React.ReactNode;
  if (background) {
    photo = (
      <div
        role="img"
        aria-label={alt}
        data-testid="phone-preview-photo"
        className="bg-no-repeat bg-brand-bg-warm"
        style={{
          ...photoStyle,
          backgroundImage: `url("${background.dataUrl}")`,
          backgroundSize: background.backgroundSize,
          backgroundPosition: background.backgroundPosition,
        }}
      />
    );
  } else if (photoUrl) {
    photo = (
      <img
        src={photoUrl}
        alt={alt}
        data-testid="phone-preview-photo"
        className="object-cover block"
        style={photoStyle}
      />
    );
  } else {
    photo = (
      <div
        className="flex flex-col items-center justify-center gap-1 bg-brand-bg-warm text-brand-text-muted border-2 border-dashed border-brand-border"
        style={photoStyle}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 40 * s }} aria-hidden="true">
          add_a_photo
        </span>
        {placeholderText && s >= 0.6 && <span className="text-xs font-medium">{placeholderText}</span>}
      </div>
    );
  }

  return (
    <div
      className="inline-flex flex-col items-center bg-white shadow-md border border-brand-border/60"
      style={{ padding: 8 * s, borderRadius: 24 * s, gap: 4 * s }}
    >
      {photo}
      <div
        className="font-bold text-[#1F1F1F] text-center leading-tight line-clamp-2 break-words"
        style={{ width, fontSize: Math.max(11, 22 * s), minHeight: 32 * s }}
      >
        {name}
      </div>
    </div>
  );
};

export default PhoneButtonPreview;
